import type { CollectionName, TicketFilterKey } from './settings-api';

/**
 * What each settings tab IS — one declaration driving the nav, the panel heading, the add form and
 * the usage column.
 *
 * ⭐ The alternative is a switch statement per surface, and the failure mode of that is a collection
 * that is reachable from the desktop nav and absent from the phone's, which nobody notices because
 * each is only ever looked at on the machine it was built on. (The shell's `views.ts` says the same
 * thing about the four main views; this is the same rule one level down.)
 */
export interface CollectionDef {
  name: CollectionName;
  /** Tab label and panel heading. */
  title: string;
  /** Used in the add form — "Add status", "New lane". */
  singular: string;
  /** One line saying what this vocabulary is FOR, shown under the heading. */
  purpose: string;
  /** Whether the row carries a `colour`. Impacts and resolutions do not — db/004 and db/007. */
  hasColour: boolean;
  /**
   * The `GET /tickets` filter that can count usage, or `null` when the API offers none.
   * ⛔ `null` means the count is UNAVAILABLE and must be printed as such — never as zero.
   */
  usageFilter: TicketFilterKey | null;
}

export const COLLECTION_DEFS: readonly CollectionDef[] = [
  {
    name: 'statuses',
    title: 'Statuses',
    singular: 'status',
    purpose:
      'The 26 workflow states. Each belongs to a lane, and the legal moves between them are the transition matrix — a separate tab, because they are rows and not code.',
    hasColour: true,
    usageFilter: 'status',
  },
  {
    name: 'lanes',
    title: 'Lanes',
    singular: 'lane',
    purpose:
      'The six groups the 26 statuses collapse into. The board is unusable without them on a phone, so a lane is structure rather than decoration.',
    hasColour: true,
    // ⭐ Counted from the STATUSES, not from tickets: `GET /tickets` has no lane filter, and "how
    // many statuses sit in this lane" is the consequence of retiring one anyway.
    usageFilter: null,
  },
  {
    name: 'types',
    title: 'Ticket types',
    singular: 'type',
    purpose: 'Story, Bug, Task, Sub-task. `is_subtask` decides which ones require a parent (db/009).',
    hasColour: false,
    usageFilter: 'type',
  },
  {
    name: 'priorities',
    title: 'Priorities',
    singular: 'priority',
    purpose: 'Highest → Lowest. Ordered by `sort_order`, which is what the icons and sorting read.',
    hasColour: true,
    usageFilter: 'priority',
  },
  {
    name: 'impacts',
    title: 'Impacts',
    singular: 'impact',
    purpose:
      'Significant / Large · Moderate / Limited. A field GitLab CE cannot hold, which is half of why PlanHub exists.',
    hasColour: false,
    usageFilter: null,
  },
  {
    name: 'resolutions',
    title: 'Resolutions',
    singular: 'resolution',
    purpose:
      'DELIVERED how — Hotfix · Bugfix · By Sprint · Immediate Fix · Database Fix. ⚠️ NOT Jira’s "done how" (Fixed / Won’t Do / Duplicate); it is a delivery route, and correcting it into Jira’s would change what every historical row means.',
    hasColour: false,
    usageFilter: null,
  },
  {
    name: 'modules',
    title: 'Modules',
    singular: 'module',
    purpose:
      'Per PROJECT — a module list is meaningless across projects, so `module_def` is keyed by project_id (db/004) and lives at /projects/{id}/modules rather than under /settings.',
    hasColour: false,
    usageFilter: null,
  },
];

export function collectionDef(name: string): CollectionDef | null {
  return COLLECTION_DEFS.find((entry) => entry.name === name) ?? null;
}

/**
 * ⭐ Throws rather than substituting a default, matching `requireView` in the shell. A tab whose
 * definition was deleted is a wiring mistake; rendering it with an invented title hides the mistake
 * behind something that looks fine.
 */
export function requireCollection(name: string): CollectionDef {
  const def = collectionDef(name);
  if (!def) throw new Error(`No settings collection is registered as "${name}" — see collections.ts`);
  return def;
}

/** The transition matrix is a tab too, but it is not a vocabulary — it has its own panel. */
export const TRANSITIONS_TAB = 'transitions';
export type SettingsTab = CollectionName | typeof TRANSITIONS_TAB;

export const DEFAULT_TAB: SettingsTab = 'statuses';

export function isSettingsTab(value: string | null | undefined): value is SettingsTab {
  if (!value) return false;
  return value === TRANSITIONS_TAB || collectionDef(value) !== null;
}
